import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import RankBadge from '../components/RankBadge'

const TEAM = [
  { name: 'Shaurya Maheshwari', role: 'Co-Founder & Editor', note: 'Believes the best argument wins, not the loudest one.' },
  { name: 'Savyaraj', role: 'Co-Founder', note: 'Built this because good writing deserves a better home.' },
]

export default function MastheadPage() {
  const [voices, setVoices] = useState([])
  const [topWriters, setTopWriters] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      const [voicesRes, writersRes] = await Promise.all([
        supabase.from('profiles').select('id, display_name, bio, column_inches, is_voice, role')
          .eq('is_voice', true).order('column_inches', { ascending: false }),
        supabase.from('profiles').select('id, display_name, column_inches, is_voice, role')
          .neq('role', 'viewer').order('column_inches', { ascending: false }).limit(10),
      ])
      setVoices(voicesRes.data || [])
      setTopWriters(writersRes.data || [])
      setLoading(false)
    }
    load()
  }, [])

  return (
    <div style={s.page}>
      <div style={s.hero}>
        <p style={s.eyebrow}>The Margins</p>
        <h1 style={s.title}>The Masthead</h1>
        <p style={s.sub}>The people who built this, the writers who define it.</p>
      </div>

      {/* Founders */}
      <section style={s.section}>
        <div style={s.sectionHeader}>
          <span style={s.sectionLabel}>Founders</span>
        </div>
        <div style={s.teamGrid}>
          {TEAM.map(t => (
            <div key={t.name} style={s.teamCard}>
              <div style={s.teamAvatar}>{t.name[0]}</div>
              <div>
                <div style={s.teamName}>{t.name}</div>
                <div style={s.teamRole}>{t.role}</div>
                <p style={s.teamNote}>{t.note}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* The Voice */}
      {!loading && voices.length > 0 && (
        <section style={s.section}>
          <div style={s.sectionHeader}>
            <span style={s.sectionLabel}>◆ The Voice</span>
            <span style={s.sectionNote}>Editorial appointment only — the highest honour on The Margins</span>
          </div>
          <div style={s.voiceGrid}>
            {voices.map(v => (
              <Link key={v.id} to={`/writer/${v.id}`} style={s.voiceCard}>
                <div style={s.voiceAvatar}>{v.display_name?.[0]?.toUpperCase()}</div>
                <div style={s.voiceName}>{v.display_name}</div>
                {v.bio && <p style={s.voiceBio}>{v.bio}</p>}
                <RankBadge columnInches={v.column_inches || 0} isVoice={true} size="sm" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Top writers by CI */}
      {!loading && topWriters.length > 0 && (
        <section style={s.section}>
          <div style={s.sectionHeader}>
            <span style={s.sectionLabel}>The Press — Top Writers</span>
            <span style={s.sectionNote}>Ranked by Column Inches</span>
          </div>
          <div style={s.leaderboard}>
            {topWriters.map((w, i) => (
              <Link key={w.id} to={`/writer/${w.id}`} style={s.leaderRow}>
                <span style={s.leaderNum}>#{i + 1}</span>
                <span style={s.leaderName}>{w.display_name}</span>
                <RankBadge columnInches={w.column_inches || 0} isVoice={w.is_voice || false} size="sm" />
                <span style={s.leaderCI}>{w.column_inches || 0} CI</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Philosophy */}
      <section style={{ ...s.section, maxWidth: 640, margin: '0 auto 4rem' }}>
        <div style={s.sectionHeader}><span style={s.sectionLabel}>What This Is</span></div>
        <p style={s.philosophy}>
          The Margins is a depth-first publishing platform. No algorithm, no virality, no performance.
          One rule: mean it. The rank system — The Press — exists to recognise writers who keep showing up,
          who back their arguments, who make readers think. The Voice is the only rank that can't be earned
          automatically. It's given to writers whose work has genuinely shaped what this place is.
        </p>
        <p style={{ ...s.philosophy, marginTop: '1rem' }}>
          If you write here, your work is yours. The Masthead doesn't own your perspective — it just
          gives it somewhere serious to land.
        </p>
      </section>
    </div>
  )
}

const s = {
  page: { paddingTop: '4.5rem', minHeight: '100vh' },
  hero: { padding: '4rem 3rem 3rem', borderBottom: '1px solid var(--rule)', maxWidth: 860, margin: '0 auto' },
  eyebrow: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.75rem' },
  title: { fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(2.5rem, 6vw, 4rem)', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '0.75rem' },
  sub: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.1rem', color: 'var(--muted)', lineHeight: 1.6 },
  section: { padding: '3rem', maxWidth: 860, margin: '0 auto' },
  sectionHeader: { display: 'flex', alignItems: 'baseline', gap: '1rem', marginBottom: '1.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid var(--rule)' },
  sectionLabel: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--accent)', fontWeight: 600 },
  sectionNote: { fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', color: 'var(--muted)', letterSpacing: '0.04em' },
  teamGrid: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  teamCard: { display: 'flex', gap: '1.25rem', alignItems: 'flex-start', padding: '1.25rem 1.5rem', background: 'var(--cream)', border: '1px solid var(--rule)', borderRadius: 10 },
  teamAvatar: { width: 48, height: 48, borderRadius: '50%', background: 'var(--accent)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: 700, flexShrink: 0 },
  teamName: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.2rem' },
  teamRole: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.5rem' },
  teamNote: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.85rem', color: 'var(--muted)', lineHeight: 1.6, margin: 0 },
  voiceGrid: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  voiceCard: { display: 'flex', gap: '1rem', alignItems: 'center', padding: '1rem 1.25rem', background: 'rgba(200,64,26,0.05)', border: '1px solid rgba(200,64,26,0.2)', borderRadius: 10, textDecoration: 'none', color: 'inherit', transition: 'all 0.2s' },
  voiceAvatar: { width: 40, height: 40, borderRadius: '50%', background: 'var(--accent)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: 700, flexShrink: 0 },
  voiceName: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1rem', fontWeight: 700, flex: 1 },
  voiceBio: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.8rem', color: 'var(--muted)', lineHeight: 1.5, margin: 0 },
  leaderboard: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  leaderRow: { display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.85rem 1.25rem', background: 'var(--cream)', border: '1px solid var(--rule)', borderRadius: 8, textDecoration: 'none', color: 'inherit', transition: 'border-color 0.15s' },
  leaderNum: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: 'var(--muted)', minWidth: 28 },
  leaderName: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.95rem', fontWeight: 600, flex: 1 },
  leaderCI: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', color: 'var(--accent)', letterSpacing: '0.06em' },
  philosophy: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.95rem', color: 'var(--muted)', lineHeight: 1.85 },
}
